import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Select, DatePicker, InputNumber, Space, message, Row, Col, Statistic, Upload, Modal } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import dayjs from 'dayjs';

const { Option } = Select;

const ItemRow = ({ field, remove, products, isInitiallyCompleted, form }) => {
  const { key, ...restField } = field;
  const quantity = Form.useWatch(['items', field.name, 'quantity'], form);
  const unitPrice = Form.useWatch(['items', field.name, 'unit_price'], form);

  useEffect(() => {
    const amount = (quantity || 0) * (unitPrice || 0);
    const items = form.getFieldValue('items');
    if (items && items[field.name]) {
      items[field.name].amount = amount;
      form.setFieldsValue({ items });
    }
  }, [quantity, unitPrice, field.name, form]);

  const handleProductChange = (productId) => {
    const selectedProduct = products.find(p => p.id === productId);
    if (selectedProduct) {
      const items = form.getFieldValue('items');
      items[field.name] = {
        ...items[field.name],
        product_id: selectedProduct.id,
        unit_price: selectedProduct.standard_price || 0,
      };
      form.setFieldsValue({ items: [...items] });
    }
  };

  return (
    <Row gutter={16} key={key} style={{ marginBottom: 8 }}>
      <Col span={8}>
        <Form.Item {...restField} name={[field.name, 'product_id']} rules={[{ required: true, message: '请选择产品' }]} noStyle>
          <Select placeholder="选择产品" onChange={handleProductChange} disabled={isInitiallyCompleted} showSearch optionFilterProp="children" style={{ width: '100%' }}>
            {products.map(product => <Option key={product.id} value={product.id}>{product.name}{product.spec ? ` (${product.spec})` : ''}</Option>)}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item {...restField} name={[field.name, 'quantity']} rules={[{ required: true, message: '请输入数量' }, { type: 'number', min: 0.01 }]} noStyle>
          <InputNumber placeholder="数量" min={0.01} precision={2} step={0.01} disabled={isInitiallyCompleted} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item {...restField} name={[field.name, 'unit_price']} rules={[{ required: true, message: '请输入单价' }, { type: 'number', min: 0 }]} noStyle>
          <InputNumber placeholder="单价" min={0} precision={2} disabled={isInitiallyCompleted} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item name={[field.name, 'amount']} noStyle>
          <InputNumber placeholder="金额" min={0} precision={2} disabled style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={4}>
        {!isInitiallyCompleted && <MinusCircleOutlined onClick={() => remove(field.name)} />}
      </Col>
    </Row>
  );
};

const SalesOrderForm = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitiallyCompleted, setIsInitiallyCompleted] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const items = Form.useWatch('items', form);

  const isEditing = !!id;
  const BUCKET_NAME = 'sales-order-attachments';

  useEffect(() => {
    if (items) {
      const newTotal = items.reduce((sum, item) => sum + (item?.amount || 0), 0);
      setTotalAmount(newTotal);
    }
  }, [items]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/v1/customers', { params: { pageSize: -1 } });
      setCustomers(response.data.data);
    } catch (error) {
      message.error('获取客户列表失败');
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get('/v1/products', { params: { pageSize: -1 } });
      setProducts(response.data.data);
    } catch (error) {
      message.error('获取产品列表失败');
    }
  }, []);

  const fetchOrder = useCallback(async () => {
    if (isEditing) {
      setLoading(true);
      try {
        const response = await api.get(`/v1/sales_orders/${id}`);
        const orderData = response.data;
        form.setFieldsValue({
          ...orderData,
          order_date: dayjs(orderData.order_date),
          items: orderData.sales_items,
          _original_paid_amount: orderData.paid_amount, // 保存原始已收款金额
        });
        if (orderData.status === 'completed' || orderData.status === 'cancelled') {
          setIsInitiallyCompleted(true);
        }

        if (orderData.attachment_urls && orderData.attachment_urls.length > 0) {
          const initialFiles = orderData.attachment_urls.map((url, index) => {
            // Extract file path from signed URL
            let filePath = url;
            try {
              const urlObj = new URL(url);
              const pathParts = urlObj.pathname.split('/');
              const bucketIndex = pathParts.findIndex(part => part === 'sales-order-attachments');
              if (bucketIndex !== -1 && bucketIndex < pathParts.length - 1) {
                filePath = pathParts.slice(bucketIndex + 1).join('/');
              }
            } catch (e) {
              console.error('Failed to parse URL:', url, e);
            }
            return {
              uid: `-${index}`,
              name: url.split('/').pop(),
              status: 'done',
              url: url,
              path: filePath,
            };
          });
          setFileList(initialFiles);
        }
      } catch (error) {
        message.error('获取销售订单详情失败');
      } finally {
        setLoading(false);
      }
    }
  }, [isEditing, id, form]);

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    if (isEditing) {
      fetchOrder();
    }
  }, [fetchCustomers, fetchProducts, isEditing, fetchOrder]);

  useEffect(() => {
    if (isInitiallyCompleted) {
      document.title = '查看销售订单';
    } else {
      document.title = isEditing ? '编辑销售订单' : '新增销售订单';
    }
  }, [isEditing, isInitiallyCompleted]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        order_date: values.order_date.format('YYYY-MM-DD'),
        attachment_urls: fileList.map(file => file.path || file.url),
      };

      // 编辑时保留原始的已收款金额
      if (isEditing) {
        payload.paid_amount = form.getFieldValue('_original_paid_amount') || 0;
        await api.put(`/v1/sales_orders/${id}`, payload);
        message.success('销售订单更新成功');
      } else {
        await api.post('/v1/sales_orders', payload);
        message.success('销售订单创建成功');
      }
      navigate('/sales/sales-orders', { state: { closeCurrent: true } });
    } catch (error) {
      message.error(error.response?.data?.error || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (status) => {
    if (isInitiallyCompleted) {
      message.warn('已完成或已取消的订单不能修改状态！');
      form.setFieldsValue({ status: 'completed' });
      return;
    }
    form.setFieldsValue({ status });
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const customRequest = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', BUCKET_NAME);

    try {
      const response = await api.post('/v1/attachments/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      file.url = response.data.url;
      file.path = response.data.path;
      onSuccess(response.data, file);
      message.success(`${file.name} 上传成功`);
    } catch (error) {
      onError(error);
      message.error(`${file.name} 上传失败`);
    }
  };

  const handleRemove = async (file) => {
    const fileUrl = file.url;
    if (fileUrl) {
      try {
        await api.delete('/v1/attachments', { data: { url: fileUrl, bucket: BUCKET_NAME } });
        message.success(`附件删除成功`);
        const newFileList = fileList.filter(item => item.uid !== file.uid);
        setFileList(newFileList);
        return true;
      } catch (error) {
        message.error(`附件删除失败: ${error.response?.data?.error || '服务器错误'}`);
        return Promise.reject(false);
      }
    }
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    return true;
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{ order_date: dayjs(), status: 'pending', items: [{}], attachment_urls: [] }}
    >
      <Form.Item name="order_no" label="订单号">
        <Input disabled placeholder={isEditing ? '' : '自动生成'} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="customer_id" label="客户" rules={[{ required: true }]}>
            <Select placeholder="选择客户" disabled={isInitiallyCompleted} showSearch optionFilterProp="children">
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.company_name}{customer.contact_person ? ` (${customer.contact_person})` : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="order_date" label="订单日期" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabled={isInitiallyCompleted} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="status" label="发货状态" rules={[{ required: true }]}>
            <Select onChange={handleStatusChange} disabled={isInitiallyCompleted}>
              <Option value="pending">待发货</Option>
              <Option value="completed">已发货</Option>
              <Option value="cancelled">已取消</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="delivery_address" label="收货地址">
        <Input.TextArea rows={2} placeholder="请输入收货地址" disabled={isInitiallyCompleted} />
      </Form.Item>

      <Form.Item name="note" label="备注">
        <Input.TextArea rows={3} disabled={isInitiallyCompleted} />
      </Form.Item>

      <h3>产品明细</h3>
      <div style={{ padding: '8px 0' }}>
        <Row gutter={16}>
          <Col span={8}><strong>产品名称</strong></Col>
          <Col span={4}><strong>数量</strong></Col>
          <Col span={4}><strong>单价</strong></Col>
          <Col span={4}><strong>金额</strong></Col>
          <Col span={4}><strong>操作</strong></Col>
        </Row>
      </div>
      <Form.List name="items">
        {(fields, { add, remove }) => (
          <>
            {fields.map(field => (
              <ItemRow key={field.key} field={field} remove={remove} products={products} isInitiallyCompleted={isInitiallyCompleted} form={form} />
            ))}
            {!isInitiallyCompleted && (
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  添加产品
                </Button>
              </Form.Item>
            )}
          </>
        )}
      </Form.List>

      <Row style={{ marginTop: 16 }}>
        <Col>
          <Statistic title="订单总金额" value={totalAmount} prefix="¥" precision={2} />
        </Col>
      </Row>

      <h3>附件</h3>
      <Form.Item>
        <Upload
          customRequest={customRequest}
          fileList={fileList}
          onPreview={handlePreview}
          onChange={handleChange}
          onRemove={handleRemove}
          listType="picture-card"
          disabled={isInitiallyCompleted}
        >
          {!isInitiallyCompleted && fileList.length < 5 && <div><PlusOutlined /><div style={{ marginTop: 8 }}>上传</div></div>}
        </Upload>
      </Form.Item>
      <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={() => setPreviewOpen(false)}>
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>

      <Form.Item style={{ marginTop: 24 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading} disabled={isInitiallyCompleted}>
            {isEditing ? '更新' : '创建'}
          </Button>
          <Button onClick={() => navigate('/sales/sales-orders', { state: { closeCurrent: true } })}>
            返回
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default SalesOrderForm;
